/**
 * Scanner-safe GoTrue mail challenges for signup, invite and email change (#2689).
 *
 * The same mechanism as recovery (`recovery.ts`, #2265) and participant sign-in
 * (`signin-challenge.ts`, #2668), extended to the three remaining GoTrue mail
 * flows rather than a new ceremony:
 *
 *   mail:     {{ .RedirectTo }}#type=<signup|invite|email_change>&token_hash={{ .TokenHash }}
 *   landing:  read + scrub the fragment in one call, hold the hash in memory
 *   exchange: POST /verify {token_hash,type} only after an explicit Continue
 *
 * A `ConfirmationURL` is a GET that spends the one-time token. Microsoft's
 * scanners fetched one within 2 s of the send on 2026-10-08 and the person's own
 * click could only fail. A fragment never reaches a server, and a scanner that
 * renders the landing still does not press Continue.
 *
 * Reading only scrubs and returns memory-owned input: no fetch, no storage, no
 * session. The key set and the type set are closed, so this reader cannot be
 * used to turn a recovery or sign-in fragment into one of these flows, and a
 * duplicate key cannot hide the purpose behind an unrelated value.
 *
 * ONE optional third key, for ONE flow (MB-SOLUM #1863 c.124186 item 3):
 *
 *   #type=invite&verification_type=recovery&token_hash=<hash>
 *
 * An invitee who pressed Continue and left before choosing a password is
 * confirmed in GoTrue, which then refuses another invite. The authorised resend
 * mints GoTrue `recovery` for exactly that identity instead. The purpose is
 * still the invitation (same landing, same first-password ceremony); the key
 * only names the verifier that spends the hash. It is admitted for `invite`
 * alone, with the value `recovery` alone, exactly once — any other value,
 * flow, duplicate or extra key is refused like every malformed challenge. The
 * ordinary two-key invite is unchanged.
 */
export const AUTH_MAIL_FLOWS = Object.freeze(['signup', 'invite', 'email_change'] as const)
export type AuthMailFlow = typeof AUTH_MAIL_FLOWS[number]

export interface AuthMailChallenge {
  flow: AuthMailFlow
  tokenHash: string
  /** Present only on an invitation whose first credential is recovered (see the header). */
  verificationType?: 'recovery'
}

export interface AuthMailChallengeReadResult {
  challenge: AuthMailChallenge | null
  error: { code: 'invalid_mail_challenge'; description: string } | null
}

const TOKEN_HASH = /^[A-Za-z0-9_-]{40,128}$/
const KEYS = Object.freeze(['type', 'token_hash'])
const RECOVERED_KEYS = Object.freeze([...KEYS, 'verification_type'])
const isFlow = (value: unknown): value is AuthMailFlow =>
  typeof value === 'string' && (AUTH_MAIL_FLOWS as readonly string[]).includes(value)

const invalid = (): AuthMailChallengeReadResult => ({
  challenge: null,
  error: { code: 'invalid_mail_challenge', description: 'That link is not valid. Request a new one.' },
})

/** Shared runtime validation for untyped hosts. */
export function isAuthMailChallenge(value: unknown): value is AuthMailChallenge {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const keys = Object.keys(value)
  const challenge = value as Partial<AuthMailChallenge>
  // The third key exists only as invite + recovery (see the header).
  const recovered = keys.length === 3 && keys.includes('verificationType')
    && challenge.flow === 'invite' && challenge.verificationType === 'recovery'
  return (keys.length === 2 || recovered) && keys.includes('flow') && keys.includes('tokenHash')
    && isFlow(challenge.flow)
    && typeof challenge.tokenHash === 'string' && TOKEN_HASH.test(challenge.tokenHash)
}

/** True when the fragment claims one of these flows AND carries a challenge.
 *  A pure question for a host gate: it neither scrubs nor returns the hash. */
export function isAuthMailChallengeArrival(
  loc: Pick<Location, 'hash'> | undefined =
    typeof window !== 'undefined' ? window.location : undefined,
): boolean {
  if (!loc?.hash || loc.hash.length < 2) return false
  const params = new URLSearchParams(loc.hash.slice(1))
  return params.getAll('type').some(isFlow) && params.has('token_hash')
}

export function readAuthMailChallengeFromHash(
  loc: Pick<Location, 'hash' | 'pathname' | 'search'> | undefined =
    typeof window !== 'undefined' ? window.location : undefined,
  hist: Pick<History, 'replaceState'> | undefined =
    typeof window !== 'undefined' ? window.history : undefined,
): AuthMailChallengeReadResult {
  const empty: AuthMailChallengeReadResult = { challenge: null, error: null }
  if (!loc?.hash || loc.hash.length < 2) return empty
  const params = new URLSearchParams(loc.hash.slice(1))
  // The legacy `#access_token…&type=signup` redirect belongs to readSignUpFromHash,
  // recovery to readRecoveryFromHash, participant sign-in to #2668. Only a
  // fragment that names one of these flows AND carries a hash is claimed here.
  if (!params.getAll('type').some(isFlow) || !params.has('token_hash')) return empty

  try {
    if (typeof hist?.replaceState !== 'function') return invalid()
    hist.replaceState(null, '', `${loc.pathname}${loc.search}`)
  } catch {
    // Never hand the bearer to application code while it remains in history.
    return invalid()
  }

  const keys = [...params.keys()]
  const recovered = params.has('verification_type')
  const allowed = recovered ? RECOVERED_KEYS : KEYS
  // Exact key count + one type + one hash leaves exactly one verifier key too;
  // isAuthMailChallenge then admits it for invite + recovery alone.
  if (keys.length !== allowed.length || keys.some((key) => !allowed.includes(key))
    || params.getAll('type').length !== 1 || params.getAll('token_hash').length !== 1) return invalid()
  const challenge = {
    flow: params.get('type'), tokenHash: params.get('token_hash'),
    ...(recovered ? { verificationType: params.get('verification_type') } : {}),
  }
  if (!isAuthMailChallenge(challenge)) return invalid()
  return { challenge: Object.freeze(challenge), error: null }
}
